import { resolveGameStorage } from "./persistence-backend.js";
import { reportStorageOperationFailure } from "./storage-errors.js";

/**
 * Hardened localStorage — opaque keys, compact field names, encoded blobs, checksum.
 * Client-only: raises the bar for casual DevTools edits; not authoritative anti-cheat.
 *
 * ## Forward compatibility
 *
 * Wire format: `SCHEMA_VERSION:base64(xor(compactJson)):checksum` (currently v2).
 *
 * **Additive fields on the current schema version** — no envelope bump:
 * - Add optional keys in the matching `compact*` / `expand*` pair; omit when unset.
 * - Loaders (`applyProgressState`, player-stats init, etc.) default missing fields.
 *
 * **New persistence domain** — add a `StorageSlot` + `OPAQUE_KEYS` entry; read/write via
 * `readStorageRecord` / `writeStorageRecord`. Same envelope; slot picks compact/validate path.
 *
 * **Breaking wire changes** — increment `SCHEMA_VERSION` and teach `decodeStorageBlob` an
 * explicit in-module migration from the previous version. Reject unknown versions (null →
 * defaults). No plain-JSON legacy keys (`catchnmerge_*`, `mergeRun_*`); those are ignored.
 *
 * Playgama / future portal cloud sync relays the encoded blob string unchanged.
 */

const LEADERBOARD_MAX_SCORE = 100000000;
const TOOL_USES_MAX = 99999;

/** @typedef {'progress'|'economy'|'bestScore'|'playerStats'|'playerName'|'playerId'|'unlockedTiers'|'soundSettings'|'layoutMode'|'skyBackground'|'movementHint'|'runTutorial'|'retention'|'shakeHint'|'healHint'} StorageSlotId */

export const StorageSlot = {
  PROGRESS: "progress",
  ECONOMY: "economy",
  BEST_SCORE: "bestScore",
  PLAYER_STATS: "playerStats",
  PLAYER_NAME: "playerName",
  PLAYER_ID: "playerId",
  UNLOCKED_TIERS: "unlockedTiers",
  SOUND_SETTINGS: "soundSettings",
  LAYOUT_MODE: "layoutMode",
  SKY_BACKGROUND: "skyBackground",
  MOVEMENT_HINT: "movementHint",
  RUN_TUTORIAL: "runTutorial",
  RETENTION: "retention",
  SHAKE_HINT: "shakeHint",
  HEAL_HINT: "healHint",
};

/** @type {Record<StorageSlotId, string>} */
const OPAQUE_KEYS = {
  progress: "kR7mQx2p",
  economy: "eC3nKm8w",
  bestScore: "vN4wHt8j",
  playerStats: "bP9sLd3c",
  playerName: "yF2nKq6m",
  playerId: "zK8mPw5q",
  unlockedTiers: "wJ5tRx1v",
  soundSettings: "hC8uZm4b",
  layoutMode: "gD3pWn7k",
  skyBackground: "sA6eYq0f",
  movementHint: "mT1iHv5n",
  runTutorial: "rU4oFw9x",
  retention: "tN2kRm6p",
  shakeHint: "sH7pKq2w",
  healHint: "hL5rFx2q",
};

const SCHEMA_VERSION = 2;
const SCHEMA_SECRET = "mr9xKp2vQn4wHt8jLb6sFd3c";
const FLASK_HP_MAX = 3;
const GEMS_MAX = 99999;
export const PLAYER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Every opaque localStorage key used by the game (for portal sync). */
export const ALL_STORAGE_KEYS = Object.values(OPAQUE_KEYS);

/**
 * @param {StorageSlotId} slot
 * @returns {string}
 */
export function getStorageKey(slot) {
  return OPAQUE_KEYS[slot] || slot;
}

/**
 * @param {string} text
 * @returns {string}
 */
function xorObfuscate(text) {
  var out = "";
  for (var i = 0; i < text.length; i++) {
    var key = SCHEMA_SECRET.charCodeAt(i % SCHEMA_SECRET.length) ^ ((i * 17 + 31) & 0xff);
    out += String.fromCharCode(text.charCodeAt(i) ^ key);
  }
  return out;
}

/**
 * @param {string} data
 * @returns {string}
 */
function digest(data) {
  var h = 0x811c9dc5;
  var s = SCHEMA_SECRET + "|" + data;
  for (var i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function encodeStorageBlob(value) {
  var json = JSON.stringify(value);
  var obfuscated = xorObfuscate(json);
  var b64 = typeof btoa === "function"
    ? btoa(obfuscated)
    : Buffer.from(obfuscated, "binary").toString("base64");
  var tag = digest(b64);
  return SCHEMA_VERSION + ":" + b64 + ":" + tag;
}

/**
 * @param {string | null | undefined} raw
 * @returns {unknown | null}
 */
export function decodeStorageBlob(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") return null;
  var parts = raw.split(":");
  if (parts.length !== 3) return null;
  var version = Number(parts[0]);
  if (version !== SCHEMA_VERSION) return null;
  var b64 = parts[1];
  var tag = parts[2];
  if (!b64 || !tag || digest(b64) !== tag) return null;
  try {
    var obfuscated = typeof atob === "function"
      ? atob(b64)
      : Buffer.from(b64, "base64").toString("binary");
    var json = xorObfuscate(obfuscated);
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * @param {Storage | null | undefined} storage
 * @param {string} key
 * @returns {string | null}
 */
function safeGetItem(storage, key) {
  if (!storage || typeof storage.getItem !== "function") return null;
  try {
    return storage.getItem(key);
  } catch (e) {
    reportStorageOperationFailure(storage, "[Storage] read failed:", e);
    return null;
  }
}

/**
 * @param {Storage | null | undefined} storage
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
function safeSetItem(storage, key, value) {
  if (!storage || typeof storage.setItem !== "function") return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (e) {
    reportStorageOperationFailure(storage, "[Storage] write failed:", e);
    return false;
  }
}

/**
 * @param {Storage | null | undefined} storage
 * @param {string} key
 */
function safeRemoveItem(storage, key) {
  if (!storage || typeof storage.removeItem !== "function") return;
  try {
    storage.removeItem(key);
  } catch (e) { /* ignore */ }
}

/**
 * @param {unknown} body
 * @returns {Record<string, unknown> | null}
 */
function compactFlaskBody(body) {
  if (!body || typeof body !== "object") return null;
  var b = body;
  var entry = { t: b.tier };
  if (b.minimized) entry.m = 1;
  if (b.physicsSizeTier != null && b.physicsSizeTier !== b.tier) entry.p = b.physicsSizeTier;
  if (b.nx != null) entry.x = b.nx;
  if (b.ny != null) entry.y = b.ny;
  if (b.vx != null && b.vx !== 0) entry.u = b.vx;
  if (b.vy != null && b.vy !== 0) entry.v = b.vy;
  if (b.angle != null && b.angle !== 0) entry.a = b.angle;
  if (b.angularVelocity != null && b.angularVelocity !== 0) entry.w = b.angularVelocity;
  return entry;
}

/**
 * @param {unknown} entry
 * @returns {Record<string, unknown> | null}
 */
function expandFlaskBody(entry) {
  if (!entry || typeof entry !== "object") return null;
  var e = entry;
  return {
    tier: e.t,
    minimized: !!e.m,
    physicsSizeTier: e.p != null ? e.p : e.t,
    nx: e.x != null ? e.x : 0,
    ny: e.y != null ? e.y : 0,
    vx: e.u != null ? e.u : 0,
    vy: e.v != null ? e.v : 0,
    angle: e.a != null ? e.a : 0,
    angularVelocity: e.w != null ? e.w : 0,
  };
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown>}
 */
function compactProgress(state) {
  var out = {
    sc: state.currentScore,
    hp: state.flaskHp,
    fb: Array.isArray(state.flaskBodies)
      ? state.flaskBodies.map(compactFlaskBody).filter(Boolean)
      : [],
    cr: state.hasCompletedARun ? 1 : 0,
    ci: state.cycleIndex,
    rs: state.resumeStage === "shake" ? 1 : 0,
    sa: state.savedAt,
  };
  if (state.boostBar != null) out.bb = state.boostBar;
  if (state.dropMode != null) out.dm = state.dropMode;
  if (state.skyDecorSeed != null) out.sd = state.skyDecorSeed;
  if (state.skyHour != null) out.sk = state.skyHour;
  if (state.victoryShownThisRun) out.vsr = 1;
  if (state.gemsCollected != null) out.g = state.gemsCollected;
  if (state.shakeUses != null) out.sh = state.shakeUses;
  if (state.bombTripleUses != null) out.bt = state.bombTripleUses;
  if (state.minimizeUses != null) out.mn = state.minimizeUses;
  if (state.bombCellUses != null) out.bc = state.bombCellUses;
  return out;
}

/**
 * @param {Record<string, unknown>} compact
 * @returns {Record<string, unknown> | null}
 */
function expandProgress(compact) {
  if (!compact || typeof compact !== "object") return null;
  var c = compact;
  var bodies = Array.isArray(c.fb)
    ? c.fb.map(expandFlaskBody).filter(Boolean)
    : [];
  var out = {
    currentScore: c.sc,
    flaskHp: c.hp,
    flaskBodies: bodies,
    hasCompletedARun: !!c.cr,
    cycleIndex: c.ci,
    resumeStage: c.rs === 1 ? "shake" : "fall",
    boostBar: c.bb,
    dropMode: c.dm,
    skyDecorSeed: c.sd,
    skyHour: c.sk,
    savedAt: c.sa,
    victoryShownThisRun: !!c.vsr,
  };
  if (c.g != null) out.gemsCollected = c.g;
  if (c.bc != null) out.bombCellUses = c.bc;
  if (c.bt != null) out.bombTripleUses = c.bt;
  if (c.mn != null) out.minimizeUses = c.mn;
  if (c.sh != null) out.shakeUses = c.sh;
  return out;
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown>}
 */
function compactEconomy(state) {
  var out = {
    g: state.gemsCollected,
    sh: state.shakeUses,
    bt: state.bombTripleUses,
    mn: state.minimizeUses,
    bc: state.bombCellUses,
  };
  if (state.savedAt != null) out.sa = state.savedAt;
  return out;
}

/**
 * @param {Record<string, unknown>} compact
 * @returns {Record<string, unknown> | null}
 */
function expandEconomy(compact) {
  if (!compact || typeof compact !== "object") return null;
  var c = compact;
  return {
    gemsCollected: c.g,
    shakeUses: c.sh,
    bombTripleUses: c.bt,
    minimizeUses: c.mn,
    bombCellUses: c.bc,
    savedAt: c.sa,
  };
}

/**
 * Build economy payload from a legacy progress record (pre-economy slot).
 * @param {Record<string, unknown>} progress
 * @returns {Record<string, unknown> | null}
 */
export function extractEconomyFromProgress(progress) {
  if (!progress || typeof progress !== "object") return null;
  var hasAny = progress.gemsCollected != null
    || progress.shakeUses != null
    || progress.bombTripleUses != null
    || progress.minimizeUses != null
    || progress.bombCellUses != null;
  if (!hasAny) return null;
  return {
    gemsCollected: progress.gemsCollected,
    shakeUses: progress.shakeUses,
    bombTripleUses: progress.bombTripleUses,
    minimizeUses: progress.minimizeUses,
    bombCellUses: progress.bombCellUses,
    savedAt: progress.savedAt,
  };
}

/**
 * Write run checkpoint + matching lifetime economy blob with the same savedAt.
 * @param {Storage | null | undefined} storage
 * @param {Record<string, unknown>} progressState
 * @param {Record<string, unknown>} economyState
 * @returns {boolean}
 */
export function writeCheckpointStorage(storage, progressState, economyState) {
  storage = resolveGameStorage(storage);
  if (!storage || !progressState || !economyState) return false;
  var savedAt = progressState.savedAt != null
    ? progressState.savedAt
    : (economyState.savedAt != null ? economyState.savedAt : Date.now());
  var progress = Object.assign({}, progressState, economyState, { savedAt: savedAt });
  var economy = Object.assign({}, economyState, { savedAt: savedAt });
  if (validateProgressState(progress) == null || validateEconomyState(economy) == null) {
    return false;
  }
  var progressOk = writeStorageRecord(storage, StorageSlot.PROGRESS, progress);
  var economyOk = writeStorageRecord(storage, StorageSlot.ECONOMY, economy);
  return progressOk && economyOk;
}

/**
 * @param {Record<string, unknown>} stats
 * @returns {Record<string, unknown>}
 */
function compactPlayerStats(stats) {
  var out = {
    ri: stats.runIndex,
    si: stats.sessionIndex,
    di: stats.dayIndex,
    mt: stats.maxTierEver,
    tp: stats.totalPlayTimeMs,
    bv: stats.buildVariant,
    lr: stats.lastLossReason,
    sl: stats.scoreAtLoss,
    bs: stats.bestScore,
    ld: stats.lastLaunchDayKey,
    hb: stats.hasLaunchedBefore ? 1 : 0,
    pb: stats.pendingNextRunIndexBump ? 1 : 0,
  };
  if (stats.victoryFeedbackSubmitted) {
    out.vfs = 1;
    out.vfv = stats.victoryFeedbackValue;
  }
  return out;
}

/**
 * @param {Record<string, unknown>} compact
 * @returns {Record<string, unknown>}
 */
function expandPlayerStats(compact) {
  var c = compact || {};
  return {
    runIndex: c.ri,
    sessionIndex: c.si,
    dayIndex: c.di,
    maxTierEver: c.mt,
    totalPlayTimeMs: c.tp,
    buildVariant: c.bv,
    lastLossReason: c.lr,
    scoreAtLoss: c.sl,
    bestScore: c.bs,
    lastLaunchDayKey: c.ld,
    hasLaunchedBefore: !!c.hb,
    pendingNextRunIndexBump: !!c.pb,
    victoryFeedbackSubmitted: !!c.vfs,
    victoryFeedbackValue: c.vfv,
  };
}

/**
 * @param {Record<string, unknown>} prefs
 * @returns {Record<string, unknown>}
 */
function compactSkyBackground(prefs) {
  return {
    p: prefs.preference,
    r: prefs.autoResolved,
  };
}

/**
 * @param {Record<string, unknown>} compact
 * @returns {Record<string, unknown>}
 */
function expandSkyBackground(compact) {
  var c = compact || {};
  return {
    preference: c.p,
    autoResolved: c.r,
  };
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown>}
 */
function compactRetention(state) {
  var out = {
    ds: state.dailyBonusStep,
    dl: state.dailyBonusLastClaimDate,
    da: state.dailyBonusAutoShownDate,
    wf: state.wheelLastFreeSpinAt,
    wa: state.wheelLastAdSpinAt,
    ga: state.gemsAdLastAt,
  };
  if (state.savedAt != null) out.sa = state.savedAt;
  return out;
}

/**
 * @param {Record<string, unknown>} compact
 * @returns {Record<string, unknown> | null}
 */
function expandRetention(compact) {
  if (!compact || typeof compact !== "object") return null;
  var c = compact;
  return {
    dailyBonusStep: c.ds,
    dailyBonusLastClaimDate: c.dl,
    dailyBonusAutoShownDate: c.da,
    wheelLastFreeSpinAt: c.wf,
    wheelLastAdSpinAt: c.wa,
    gemsAdLastAt: c.ga,
    savedAt: c.sa,
  };
}

/**
 * @param {StorageSlotId} slot
 * @param {unknown} value
 * @returns {unknown}
 */
function compactForSlot(slot, value) {
  if (slot === StorageSlot.PROGRESS) return compactProgress(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.ECONOMY) return compactEconomy(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.PLAYER_STATS) return compactPlayerStats(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.SKY_BACKGROUND) return compactSkyBackground(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.RETENTION) return compactRetention(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.SOUND_SETTINGS) {
    var s = /** @type {Record<string, unknown>} */ (value);
    return { b: s.bgmVolume, x: s.sfxVolume };
  }
  if (slot === StorageSlot.SHAKE_HINT || slot === StorageSlot.HEAL_HINT) {
    var hint = /** @type {Record<string, unknown>} */ (value || {});
    return { d: hint.dismissed === true };
  }
  return value;
}

/**
 * @param {StorageSlotId} slot
 * @param {unknown} value
 * @returns {unknown}
 */
function expandForSlot(slot, value) {
  if (slot === StorageSlot.PROGRESS) return expandProgress(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.ECONOMY) return expandEconomy(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.PLAYER_STATS) return expandPlayerStats(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.SKY_BACKGROUND) return expandSkyBackground(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.RETENTION) return expandRetention(/** @type {Record<string, unknown>} */ (value));
  if (slot === StorageSlot.SOUND_SETTINGS) {
    var s = /** @type {Record<string, unknown>} */ (value || {});
    return { bgmVolume: s.b, sfxVolume: s.x };
  }
  if (slot === StorageSlot.SHAKE_HINT || slot === StorageSlot.HEAL_HINT) {
    var hintIn = /** @type {Record<string, unknown>} */ (value || {});
    return { dismissed: hintIn.d === true || hintIn.dismissed === true };
  }
  return value;
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown> | null}
 */
export function validateProgressState(state) {
  if (!state || typeof state !== "object") return null;
  var score = Math.floor(Number(state.currentScore) || 0);
  var hpRaw = state.flaskHp;
  var hp = isFinite(Number(hpRaw)) ? Math.floor(Number(hpRaw)) : FLASK_HP_MAX;
  if (!isFinite(score) || score < 0 || score > LEADERBOARD_MAX_SCORE) return null;
  if (!isFinite(hp) || hp < 0 || hp > FLASK_HP_MAX) return null;
  var resumeStage = state.resumeStage === "shake" ? "shake" : "fall";
  var flaskBodies = Array.isArray(state.flaskBodies) ? state.flaskBodies : [];
  return Object.assign({}, state, {
    resumeStage: resumeStage,
    flaskHp: hp,
    currentScore: score,
    flaskBodies: flaskBodies,
  });
}

function normalizeToolUses(value) {
  var n = Math.floor(Number(value) || 0);
  if (!isFinite(n) || n < 0 || n > TOOL_USES_MAX) return null;
  return n;
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown> | null}
 */
export function validateEconomyState(state) {
  if (!state || typeof state !== "object") return null;
  var gems = Math.floor(Number(state.gemsCollected) || 0);
  if (!isFinite(gems) || gems < 0 || gems > GEMS_MAX) return null;
  var shakeUses = normalizeToolUses(state.shakeUses);
  var bombTripleUses = normalizeToolUses(state.bombTripleUses);
  var minimizeUses = normalizeToolUses(state.minimizeUses);
  var bombCellUses = normalizeToolUses(state.bombCellUses);
  if (shakeUses == null || bombTripleUses == null || minimizeUses == null || bombCellUses == null) {
    return null;
  }
  return {
    gemsCollected: gems,
    shakeUses: shakeUses,
    bombTripleUses: bombTripleUses,
    minimizeUses: minimizeUses,
    bombCellUses: bombCellUses,
  };
}

/**
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown> | null}
 */
export function validateRetentionState(state) {
  if (!state || typeof state !== "object") return null;
  var step = Math.floor(Number(state.dailyBonusStep) || 1);
  if (!isFinite(step) || step < 1 || step > 7) return null;
  var lastClaim = state.dailyBonusLastClaimDate;
  var autoShown = state.dailyBonusAutoShownDate;
  if (lastClaim != null && typeof lastClaim !== "string") return null;
  if (autoShown != null && typeof autoShown !== "string") return null;
  var wf = Math.floor(Number(state.wheelLastFreeSpinAt) || 0);
  var wa = Math.floor(Number(state.wheelLastAdSpinAt) || 0);
  var ga = Math.floor(Number(state.gemsAdLastAt) || 0);
  if (!isFinite(wf) || wf < 0 || !isFinite(wa) || wa < 0 || !isFinite(ga) || ga < 0) return null;
  return {
    dailyBonusStep: step,
    dailyBonusLastClaimDate: lastClaim || "",
    dailyBonusAutoShownDate: autoShown || "",
    wheelLastFreeSpinAt: wf,
    wheelLastAdSpinAt: wa,
    gemsAdLastAt: ga,
  };
}

/**
 * @param {StorageSlotId} slot
 * @param {unknown} value
 * @returns {boolean}
 */
function validateForSlot(slot, value) {
  if (slot === StorageSlot.PROGRESS) return validateProgressState(/** @type {Record<string, unknown>} */ (value)) != null;
  if (slot === StorageSlot.ECONOMY) return validateEconomyState(/** @type {Record<string, unknown>} */ (value)) != null;
  if (slot === StorageSlot.RETENTION) return validateRetentionState(/** @type {Record<string, unknown>} */ (value)) != null;
  if (slot === StorageSlot.BEST_SCORE) {
    var n = Math.floor(Number(value) || 0);
    return isFinite(n) && n >= 0 && n <= LEADERBOARD_MAX_SCORE;
  }
  if (slot === StorageSlot.UNLOCKED_TIERS) {
    if (!Array.isArray(value)) return false;
    for (var i = 0; i < value.length; i++) {
      var t = Math.floor(Number(value[i]));
      if (!isFinite(t) || t < 0 || t > 64) return false;
    }
    return true;
  }
  if (slot === StorageSlot.SOUND_SETTINGS) {
    if (!value || typeof value !== "object") return false;
    var s = /** @type {Record<string, unknown>} */ (value);
    var bgm = Number(s.bgmVolume);
    var sfx = Number(s.sfxVolume);
    return isFinite(bgm) && bgm >= 0 && bgm <= 1 && isFinite(sfx) && sfx >= 0 && sfx <= 1;
  }
  if (slot === StorageSlot.PLAYER_ID) {
    return typeof value === "string" && PLAYER_ID_PATTERN.test(value);
  }
  if (slot === StorageSlot.SHAKE_HINT || slot === StorageSlot.HEAL_HINT) {
    return !!(value && typeof value === "object" && /** @type {Record<string, unknown>} */ (value).dismissed === true);
  }
  return value != null;
}

/**
 * @param {Storage | null | undefined} storage
 * @param {StorageSlotId} slot
 * @returns {unknown | null}
 */
export function readStorageRecord(storage, slot) {
  storage = resolveGameStorage(storage);
  if (!storage) return null;

  var raw = safeGetItem(storage, getStorageKey(slot));
  if (raw == null) return null;

  var decoded = decodeStorageBlob(raw);
  if (decoded == null) return null;
  var expanded = expandForSlot(slot, decoded);
  if (!validateForSlot(slot, expanded)) return null;
  return expanded;
}

/**
 * @param {Storage | null | undefined} storage
 * @param {StorageSlotId} slot
 * @param {unknown} value
 */
export function writeStorageRecord(storage, slot, value) {
  storage = resolveGameStorage(storage);
  if (!storage || value == null) return false;
  if (!validateForSlot(slot, value)) return false;
  var compact = compactForSlot(slot, value);
  var encoded = encodeStorageBlob(compact);
  return safeSetItem(storage, getStorageKey(slot), encoded);
}

/**
 * @param {Storage | null | undefined} storage
 * @param {StorageSlotId} slot
 */
export function removeStorageRecord(storage, slot) {
  storage = resolveGameStorage(storage);
  if (!storage) return;
  safeRemoveItem(storage, getStorageKey(slot));
}

/**
 * @param {Storage | null | undefined} storage
 * @param {StorageSlotId} slot
 * @returns {string | null}
 */
export function readStorageEncoded(storage, slot) {
  storage = resolveGameStorage(storage);
  if (!storage) return null;
  var raw = safeGetItem(storage, getStorageKey(slot));
  if (raw == null || decodeStorageBlob(raw) == null) return null;
  return raw;
}

/** Re-export opaque keys for modules that exported storage key constants. */
export const PROGRESS_STORAGE_KEY = getStorageKey(StorageSlot.PROGRESS);
export const ECONOMY_STORAGE_KEY = getStorageKey(StorageSlot.ECONOMY);
export const BEST_SCORE_STORAGE_KEY = getStorageKey(StorageSlot.BEST_SCORE);
export const PLAYER_STATS_STORAGE_KEY = getStorageKey(StorageSlot.PLAYER_STATS);
export const PLAYER_NAME_STORAGE_KEY = getStorageKey(StorageSlot.PLAYER_NAME);
export const PLAYER_ID_STORAGE_KEY = getStorageKey(StorageSlot.PLAYER_ID);
export const UNLOCKED_TIERS_STORAGE_KEY = getStorageKey(StorageSlot.UNLOCKED_TIERS);
export const SOUND_SETTINGS_STORAGE_KEY = getStorageKey(StorageSlot.SOUND_SETTINGS);
export const LAYOUT_MODE_STORAGE_KEY = getStorageKey(StorageSlot.LAYOUT_MODE);
export const SKY_BACKGROUND_PREFS_STORAGE_KEY = getStorageKey(StorageSlot.SKY_BACKGROUND);
export const MOVEMENT_HINT_STORAGE_KEY = getStorageKey(StorageSlot.MOVEMENT_HINT);
export const RUN_TUTORIAL_STORAGE_KEY = getStorageKey(StorageSlot.RUN_TUTORIAL);
export const SHAKE_HINT_STORAGE_KEY = getStorageKey(StorageSlot.SHAKE_HINT);